import { encodeGeohash, toRFC3339UTC } from './geohashUtil';

const now = Date.now();
const hour = 3600000;

function regionalThread(id, author, planet, title, body, lat, lon, extra = {}) {
  const gh = encodeGeohash(lat, lon);
  const t = new Date(now - id * hour);
  return {
    id,
    author_user_id: author,
    planet_id: planet,
    kind: 'regional',
    has_gps: 1,
    gps_verified: extra.gps_verified ?? 0,
    arrival_based: extra.arrival_based ?? 0,
    title,
    body,
    latitude: lat,
    longitude: lon,
    geohash: gh,
    view_count: extra.view_count ?? id * 3,
    like_count: extra.like_count ?? id,
    comment_count: extra.comment_count ?? 0,
    deleted_at: null,
    created_at: toRFC3339UTC(t),
    updated_at: toRFC3339UTC(t),
    edited_at: null,
  };
}

function generalThread(id, author, title, body, extra = {}) {
  const t = new Date(now - id * hour * 2);
  return {
    id,
    author_user_id: author,
    planet_id: extra.planet_id ?? null,
    kind: 'general',
    has_gps: 0,
    gps_verified: 0,
    arrival_based: 0,
    title,
    body,
    latitude: null,
    longitude: null,
    geohash: null,
    view_count: extra.view_count ?? id * 5,
    like_count: extra.like_count ?? 0,
    comment_count: extra.comment_count ?? 0,
    deleted_at: null,
    created_at: toRFC3339UTC(t),
    updated_at: toRFC3339UTC(t),
    edited_at: null,
  };
}

/** ThreadJSON-compatible seed rows */
export const SEED_THREADS = [
  regionalThread(1, 1001, 1, 'Gangnam morning', 'Coffee shop queue is long today.', 37.4979, 127.0276, {
    gps_verified: 1,
    comment_count: 2,
    like_count: 4,
  }),
  regionalThread(2, 1002, 1, 'Han River walk', 'Beautiful sunset along the river.', 37.5285, 126.932, {
    arrival_based: 1,
    like_count: 12,
  }),
  regionalThread(3, 1003, 1, 'Hongdae night', 'Street performers everywhere.', 37.5563, 126.9236),
  regionalThread(4, 1001, 1, 'Shibuya crossing', 'Peak hour crowd in Tokyo.', 35.6595, 139.7004, {
    planet_id: 2,
    comment_count: 1,
  }),
  regionalThread(5, 1004, 2, 'Times Square', 'Billboards lighting up NYC.', 40.758, -73.9855, {
    like_count: 8,
    view_count: 120,
  }),
  regionalThread(6, 1005, 2, 'Central Park', 'Picnic weather is perfect.', 40.7829, -73.9654),
  regionalThread(7, 1002, 1, 'Seoul Station', 'KTX delay 10 minutes.', 37.5547, 126.9706, {
    gps_verified: 1,
  }),
  generalThread(8, 1001, 'Product feedback', 'Would love dark mode in settings.'),
  generalThread(9, 1003, 'Welcome thread', 'Hello Fancian community!'),
  generalThread(10, 1006, 'Bug report draft', 'Map tiles flicker on zoom.', { comment_count: 1 }),
  regionalThread(11, 1007, 1, 'Busan beach', 'Haeundae waves are calm.', 35.1587, 129.1604),
  regionalThread(12, 1008, 1, 'Jeju olle trail', 'Hiking segment 7 done.', 33.4996, 126.5312, {
    arrival_based: 1,
    gps_verified: 1,
  }),
  regionalThread(13, 1010, 3, 'London pub crawl', 'Cozy ale houses in Soho.', 51.5074, -0.1278, { like_count: 6 }),
  regionalThread(14, 1011, 3, 'Paris bistro lunch', 'Steak frites near the Seine.', 48.8566, 2.3522, { gps_verified: 1 }),
  regionalThread(15, 1012, 3, 'Berlin startup meetup', 'Founders networking at Kreuzberg.', 52.52, 13.405),
  regionalThread(16, 1013, 3, 'Colosseum sunrise', 'Early walk before the crowds.', 41.9028, 12.4964, { arrival_based: 1 }),
  regionalThread(17, 1014, 4, 'Dubai marina walk', 'Yacht lights at dusk.', 25.2048, 55.2708, { view_count: 88 }),
  regionalThread(18, 1015, 4, 'Singapore hawker stall', 'Chicken rice queue is worth it.', 1.3521, 103.8198, { like_count: 15 }),
  regionalThread(19, 1016, 5, 'Opera House view', 'Harbour bridge photo spot.', -33.8688, 151.2093, { gps_verified: 1 }),
  regionalThread(20, 1017, 6, 'São Paulo street art', 'Vila Madalena murals everywhere.', -23.5505, -46.6333),
  regionalThread(21, 1018, 2, 'Mexico City tacos', 'Late-night al pastor stand.', 19.4326, -99.1332, { like_count: 9 }),
  regionalThread(22, 1019, 2, 'Toronto lakeshore', 'Bike path along Lake Ontario.', 43.6532, -79.3832),
  regionalThread(23, 1020, 2, 'LA beach volleyball', 'Santa Monica courts are packed.', 34.0522, -118.2437, { view_count: 64 }),
  regionalThread(24, 1021, 7, 'Cairo bazaar', 'Spice stalls in Khan el-Khalili.', 30.0444, 31.2357, { arrival_based: 1 }),
  regionalThread(25, 1022, 7, 'Table Mountain hike', 'Clouds rolling over Cape Town.', -33.9249, 18.4241, { gps_verified: 1 }),
  regionalThread(26, 1023, 4, 'Bosphorus ferry', 'Sunset crossing Istanbul.', 41.0082, 28.9784, { like_count: 11 }),
  regionalThread(27, 1024, 4, 'Bangkok night market', 'Pad thai and mango sticky rice.', 13.7563, 100.5018),
  regionalThread(28, 1025, 9, 'Hong Kong skyline', 'Peak tram view at night.', 22.3193, 114.1694, { view_count: 95 }),
  regionalThread(29, 1026, 3, 'Barcelona tapas crawl', 'Gothic Quarter small plates.', 41.3851, 2.1734),
  regionalThread(30, 1027, 3, 'Amsterdam canal ride', 'Renting bikes by the water.', 52.3676, 4.9041, { gps_verified: 1 }),
  regionalThread(31, 1028, 4, 'Mumbai street chaat', 'Vendors near Chowpatty Beach.', 19.076, 72.8777),
  regionalThread(32, 1029, 5, 'Melbourne laneway coffee', 'Flat white in a hidden alley.', -37.8136, 144.9631, { like_count: 7 }),
];
